import * as Api from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AIAgentTemplateAttachments } from '../../../../admin/containers/AIAgentTemplates/components/AIAgentTemplateAttachments';
import { availableModels, verticalOptions } from '../../../../admin/containers/AIAgentTemplates/constants';
import { IAIAgentTemplate, IAIAgentTemplateAttachment } from '../../../../extViewmodels/sdk/models/AI';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useAIAgentTemplateQuery } from '../../../../queries/AIAgentTemplate';
import { useCloneAIAgentTemplateMutation } from '../../../../queries/AIAgentTemplate/useCloneAIAgentTemplateMutation';
import { useCreateAIAgentTemplateMutation } from '../../../../queries/AIAgentTemplate/useCreateAIAgentTemplateMutation';
import { useUpdateAIAgentTemplateMutation } from '../../../../queries/AIAgentTemplate/useUpdateAIAgentTemplateMutation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import { TextArea } from '../../../../web/components-v2/TextArea';
import { Button } from '../../../../web/components/Button';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { TextInput } from '../../../../web/components/TextInput';
import { bs } from '../../../../web/styles/styles';
import { BasicPage } from '../../BasicPage';
import { styles } from './detailStyles';

// Sales-related conversation types only
const SALES_CONVERSATION_TYPES = [
	{ value: Api.AIConversationType.SalesLiveCoaching, label: 'Sales Live Coaching' },
	{ value: Api.AIConversationType.SalesPostCallCoaching, label: 'Sales Post Call Coaching' },
	{ value: Api.AIConversationType.SalesDemoCallCoaching, label: 'Sales Demo Call Coaching' },
	{ value: Api.AIConversationType.SalesPracticeCall, label: 'Sales Practice Call' },
];

interface FormData {
	name: string;
	description: string;
	objectivePrompt: string;
	technicalPrompt: string;
	model: string;
	conversationType: string;
	selectors: Record<string, string>;
	variables: Record<string, string>;
	attachments: IAIAgentTemplateAttachment[];
	isActive: boolean;
}

const EMPTY_FORM_DATA: FormData = {
	name: '',
	description: '',
	objectivePrompt: '',
	technicalPrompt: '',
	model: '',
	conversationType: '',
	selectors: {},
	variables: {},
	attachments: [],
	isActive: false,
};

function getInitialFormData(template: IAIAgentTemplate | undefined, isCloneMode: boolean): FormData {
	if (!template) {
		return EMPTY_FORM_DATA;
	}
	return {
		name: isCloneMode ? `${template.name} (Copy)` : template.name,
		description: template.description || '',
		objectivePrompt: template.objectivePrompt || '',
		technicalPrompt: template.technicalPrompt || '',
		model: template.model || '',
		conversationType: template.conversationType || '',
		selectors: template.selectors || {},
		variables: template.variables || {},
		attachments: template.attachments || [],
		isActive: template.isActive || false,
	};
}

interface AgentTemplateFormProps {
	initialData: FormData;
	isNewMode: boolean;
	isCloneMode: boolean;
	templateId: string;
	originalTemplateId?: string;
}

function AgentTemplateForm({
	initialData,
	isNewMode,
	isCloneMode,
	templateId,
	originalTemplateId,
}: AgentTemplateFormProps) {
	const history = useHistory();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();

	const [formData, setFormData] = React.useState<FormData>(initialData);
	const [formErrors, setFormErrors] = React.useState<Record<string, string>>({});
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	// Mutations
	const updateMutation = useUpdateAIAgentTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['aiAgentTemplatesQuery'] });
			toast.success('Sales AI Agent updated successfully');
			history.push('/settings/agentTemplates');
		},
		onError: error => {
			errorMessages.pushApiError(error);
			setIsSubmitting(false);
		},
	});

	const createMutation = useCreateAIAgentTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['aiAgentTemplatesQuery'] });
			toast.success('Sales AI Agent created successfully');
			history.push('/settings/agentTemplates');
		},
		onError: error => {
			errorMessages.pushApiError(error);
			setIsSubmitting(false);
		},
	});

	const cloneMutation = useCloneAIAgentTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['aiAgentTemplatesQuery'] });
			toast.success('Sales AI Agent cloned successfully');
			history.push('/settings/agentTemplates');
		},
		onError: error => {
			errorMessages.pushApiError(error);
			setIsSubmitting(false);
		},
	});

	const validateForm = () => {
		const errors: Record<string, string> = {};

		if (!formData.name.trim()) {
			errors.name = 'Name is required';
		}
		if (!formData.objectivePrompt.trim()) {
			errors.objectivePrompt = 'Objective prompt is required';
		}
		if (!formData.model.trim()) {
			errors.model = 'Model is required';
		}
		if (!formData.conversationType.trim()) {
			errors.conversationType = 'Agent type is required';
		}

		return errors;
	};

	const handleSubmit = async () => {
		const errors = validateForm();
		setFormErrors(errors);

		if (Object.keys(errors).length > 0) {
			toast.error('Please fix the errors before submitting');
			return;
		}

		setIsSubmitting(true);

		const data = {
			name: formData.name.trim(),
			description: formData.description?.trim() || null,
			objectivePrompt: formData.objectivePrompt.trim(),
			technicalPrompt: formData.technicalPrompt?.trim() || null,
			model: formData.model,
			conversationType: formData.conversationType as Api.AIConversationType,
			selectors: Object.keys(formData.selectors).length > 0 ? formData.selectors : null,
			variables: Object.keys(formData.variables).length > 0 ? formData.variables : {},
			attachments: formData.attachments.length > 0 ? formData.attachments : null,
			isActive: formData.isActive,
		};

		if (isCloneMode && originalTemplateId) {
			await cloneMutation.mutateAsync({ originalId: originalTemplateId, data });
		} else if (isNewMode) {
			await createMutation.mutateAsync({ data });
		} else {
			await updateMutation.mutateAsync({ id: templateId, data });
		}
	};

	const handleCancel = () => {
		history.push('/settings/agentTemplates');
	};

	const updateFormData = (field: keyof FormData, value: FormData[keyof FormData]) => {
		setFormData(prev => ({ ...prev, [field]: value }));
		// Clear error when field is edited
		if (formErrors[field]) {
			setFormErrors(prev => {
				const newErrors = { ...prev };
				delete newErrors[field];
				return newErrors;
			});
		}
	};

	const pageTitle = isNewMode ? 'Create Sales AI Agent' : isCloneMode ? 'Clone Sales AI Agent' : 'Edit Sales AI Agent';

	return (
		<BasicPage title={pageTitle}>
			<div className={css(styles.container)}>
				<form className={css(styles.form)}>
					{/* Agent Type - First field to match admin */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Agent Type</label>
						<Select
							value={formData.conversationType as Api.AIConversationType}
							onValueChange={value => updateFormData('conversationType', value)}
							disabled={isSubmitting}
							items={SALES_CONVERSATION_TYPES}
						>
							<SelectTrigger styles={[bs.wFull]}>
								<SelectValue placeholder='Select agent type' />
							</SelectTrigger>
							<SelectContent>
								{SALES_CONVERSATION_TYPES.map(type => (
									<SelectItem key={type.value} value={type.value}>
										{type.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{formErrors.conversationType && (
							<div className={css(styles.errorMessage)}>{formErrors.conversationType}</div>
						)}
					</div>

					{/* Name */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Name</label>
						<TextInput
							type='text'
							value={formData.name}
							onChange={e => updateFormData('name', e.target.value)}
							placeholder='Enter prompt name'
							disabled={isSubmitting}
						/>
						{formErrors.name && <div className={css(styles.errorMessage)}>{formErrors.name}</div>}
					</div>

					{/* Description */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Description</label>
						<TextArea
							styles={[styles.textArea]}
							value={formData.description}
							onChange={e => updateFormData('description', e.target.value)}
							placeholder='Enter template description'
							disabled={isSubmitting}
						/>
						{formErrors.description && <div className={css(styles.errorMessage)}>{formErrors.description}</div>}
					</div>

					{/* Model */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Model</label>
						<Select
							value={formData.model}
							onValueChange={value => updateFormData('model', value)}
							disabled={isSubmitting}
							items={availableModels}
						>
							<SelectTrigger styles={[bs.wFull]}>
								<SelectValue placeholder='Select AI model' />
							</SelectTrigger>
							<SelectContent>
								{availableModels.map(model => (
									<SelectItem key={model.value} value={model.value}>
										{model.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{formErrors.model && <div className={css(styles.errorMessage)}>{formErrors.model}</div>}
					</div>

					{/* Objective Prompt */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Objective Prompt</label>
						<TextArea
							styles={[styles.objectivePromptArea]}
							value={formData.objectivePrompt}
							onChange={e => updateFormData('objectivePrompt', e.target.value)}
							placeholder='Enter objective prompt'
							disabled={isSubmitting}
						/>
						{formErrors.objectivePrompt && <div className={css(styles.errorMessage)}>{formErrors.objectivePrompt}</div>}
					</div>

					{/* Technical Prompt */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Technical Prompt</label>
						<TextArea
							styles={[styles.technicalPromptArea]}
							value={formData.technicalPrompt}
							onChange={e => updateFormData('technicalPrompt', e.target.value)}
							placeholder='Enter technical prompt'
							disabled={isSubmitting}
						/>
						{formErrors.technicalPrompt && <div className={css(styles.errorMessage)}>{formErrors.technicalPrompt}</div>}
					</div>

					{/* Vertical - Show for all Sales types */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Vertical (Optional)</label>
						<Select
							value={formData.selectors?.Vertical || ''}
							onValueChange={value =>
								updateFormData('selectors', value ? { ...formData.selectors, Vertical: value } : {})
							}
							disabled={isSubmitting}
							items={verticalOptions}
						>
							<SelectTrigger styles={[bs.wFull]}>
								<SelectValue placeholder='Select vertical (optional)' />
							</SelectTrigger>
							<SelectContent>
								{verticalOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Attachments */}
					<div className={css(styles.formGroup)}>
						<label className={css(styles.label)}>Attachments</label>
						<AIAgentTemplateAttachments
							attachments={formData.attachments}
							onChange={attachments => updateFormData('attachments', attachments)}
						/>
					</div>
				</form>

				{/* Actions */}
				<div className={css(styles.actions)}>
					<Button onClick={handleCancel} kind='secondary' disabled={isSubmitting}>
						Cancel
					</Button>
					<Button onClick={handleSubmit} kind='primary' disabled={isSubmitting}>
						{isSubmitting ? 'Saving...' : isNewMode ? 'Create' : isCloneMode ? 'Clone' : 'Save'}
					</Button>
				</div>
			</div>
		</BasicPage>
	);
}

export function AgentTemplateDetail() {
	const location = useLocation();
	const { id } = useParams<{ id: string }>();

	const isNewMode = id === 'new';
	const isCloneMode = new URLSearchParams(location.search).get('mode') === 'clone';

	const {
		data: aiAgentTemplate,
		isPending,
		isFetching,
	} = useAIAgentTemplateQuery({
		id: isNewMode ? '' : id,
		enabled: !isNewMode && !!id,
	});

	// Show loading state for edit/clone modes until data is ready
	// Use isFetching to also wait during refetch (e.g., when navigating between templates)
	// Check that the loaded template matches the requested id to avoid stale data issues
	const isDataReady = aiAgentTemplate && aiAgentTemplate.id === id;
	if (!isNewMode && (isPending || isFetching || !isDataReady)) {
		return (
			<BasicPage title='Loading...'>
				<div className={css(styles.loadingContainer)}>
					<LoadingSpinner />
				</div>
			</BasicPage>
		);
	}

	// Render form only after data is available (or immediately for new mode)
	// Using key={id} forces React to remount the form when navigating to a different template,
	// ensuring the form state is re-initialized with the correct values
	return (
		<AgentTemplateForm
			key={id}
			initialData={getInitialFormData(aiAgentTemplate, isCloneMode)}
			isNewMode={isNewMode}
			isCloneMode={isCloneMode}
			templateId={id}
			originalTemplateId={aiAgentTemplate?.id}
		/>
	);
}
